import React, { useCallback, useEffect, useMemo } from "react"
import { View, forwardRef, ViewProps, createStyles } from "@design-system/core"
import { useDimensions } from "@design-system/hooks"
import { __DEV__ } from "@design-system/utils"
import { Portal } from "@design-system/portal"
import { ModalContextProvider } from "./ModalContext"
import { ModalSheetProps } from "./types"
import { ModalSheetOverlay } from "./ModalSheetOverlay"

export const ModalZIndex = 1002
export const HalfSheetZIndex = 1003
export const FullSheetZIndex = 1004

export const ModalSheet = forwardRef<ModalSheetProps, "div">((props, ref) => {
  const {
    isTouch,
    isOpen,
    withMask,
    align: alignProp,
    scrollBehavior,
    closeOnOverlayClick,
    closeOnEsc,
    onClose,
    onOverlayClick,
    onEsc,
    children,
    style,
    accessibilityRole,
    overlayTestID,
    ...others
  } = props
  const { windowWidth } = useDimensions()
  let widthSmall = isTouch === true
  let align = alignProp
  if (isTouch === undefined) {
    widthSmall = windowWidth <= 600
  }

  if (align === undefined) {
    align = isTouch ? "bottom" : "center"
  }

  const zIndex = useMemo(() => {
    if (!widthSmall) {
      return ModalZIndex
    }
    if (align === "full") {
      return FullSheetZIndex
    }
    return HalfSheetZIndex
  }, [widthSmall, align])

  const alignStyle = useMemo(() => {
    if (!widthSmall) {
      return [
        align === "bottom" && styles.alignCenter,
        align === "center" && styles.alignCenter,
        align === "full" && styles.alignCenter,
      ]
    }
    return [
      align === "bottom" && styles.alignBottom,
      align === "center" && styles.alignCenter,
      align === "full" && styles.alignFull,
    ]
  }, [align, widthSmall])

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()

      if (closeOnOverlayClick) {
        onClose?.()
      }

      onOverlayClick?.()
    },
    [onClose, closeOnOverlayClick, onOverlayClick],
  )

  const handleEsc = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()

        if (closeOnEsc) {
          onClose?.()
        }

        onEsc?.()
      }
    },
    [closeOnEsc, onClose, onEsc],
  )

  const handleContainerClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])

  const context = {
    isOpen,
    widthSmall,
    scrollBehavior,
    onClose,
    onOverlayClick,
    onEsc,
  }

  return (
    <ModalContextProvider value={context}>
      {isOpen && (
        <Portal zIndex={zIndex}>
          <ModalSheetOverlay
            accessibilityRole={accessibilityRole}
            withMask={withMask}
            scrollable={scrollBehavior === "overlay"}
            style={[styles.root, alignStyle]}
            onClick={handleOverlayClick}
            onKeyDown={handleEsc}
            testID={overlayTestID}
          >
            <View
              style={[
                styles.container,
                scrollBehavior === "container" && styles.containerScroll,
                scrollBehavior !== "overlay" &&
                  align !== "full" &&
                  styles.containerMaxHeight,
                align === "bottom" && styles.containerBottom,
                align === "full" && styles.containerFull,
                isTouch && styles.containerTouch,
                style,
              ]}
              onClick={handleContainerClick}
              {...others}
            >
              {children}
            </View>
          </ModalSheetOverlay>
        </Portal>
      )}
    </ModalContextProvider>
  )
})

if (__DEV__) {
  ModalSheet.displayName = "ModalSheet"
}

ModalSheet.defaultProps = {
  scrollBehavior: "container",
  align: "bottom",
  withMask: true,
  closeOnOverlayClick: true,
  closeOnEsc: true,
}

const styles = createStyles((theme) => ({
  root: {
    alignSelf: "center",
  },
  container: {
    flexDirection: "column",
    outlineWidth: 0,
    borderRadius: theme.borderRadii.medium,
    backgroundColor: theme.backgroundColors.default,
    width: "100%",
    maxWidth: 600,
    marginVertical: theme.spaces.spacing64,
  },
  containerScroll: {
    overflowY: "auto",
  },
  containerMaxHeight: {
    maxHeight: "calc(100vh - 128px)",
  },
  containerBottom: {
    paddingBottom: 0,
    marginBottom: 0,
    borderTopLeftRadius: theme.borderRadii.medium,
    borderTopRightRadius: theme.borderRadii.medium,
    borderBottomLeftRadius: theme.borderRadii.none,
    borderBottomRightRadius: theme.borderRadii.none,
  },
  containerTouch: {
    maxWidth: "100%",
  },
  containerFull: {
    maxWidth: "100%",
    borderRadius: theme.borderRadii.none,
    marginVertical: 0,
  },
  alignBottom: {
    alignItems: "flex-end",
  },
  alignCenter: {
    alignItems: "center",
    maxWidth: 600,
  },
  alignFull: {
    alignItems: "stretch",
  },
  fullSheet: {
    marginVertical: 0,
  },
}))
